import React, { useEffect, useRef } from 'react';
import { Controller, type Control, type UseFormSetValue, type UseFormWatch } from 'react-hook-form';
import type { InputRef } from 'antd';
import dayjs from 'dayjs';

import { AppCheckbox, AppRadio, AppRadioGroup } from '@/components/atoms';
import { CollapsibleCard } from '@/components/molecules';
import { RA2NDatePicker } from '@/components/organisms';
import { JA_JP } from '@/components/organisms/RA2NDatePicker';
import { parseDate, parseFormat } from '@/components/organisms/RA2NDatePicker/datepickerLogic';
import type { RezeptInvoicePrintOptionsFormValues } from '@/schemas/rezeptInvoicePrintOptionsSchema';
import { useTheme } from '@shared/providers/theme/ThemeContext';
import { SERVICE_TYPE_R2 } from '@/constants/serviceType';

const DATEPICKER_FORMAT = parseFormat(JA_JP.format);

export interface RezeptInvoicePrintOptionsPanelProps {
  control: Control<RezeptInvoicePrintOptionsFormValues>;
  watch: UseFormWatch<RezeptInvoicePrintOptionsFormValues>;
  setValue: UseFormSetValue<RezeptInvoicePrintOptionsFormValues>;
  /** Current search 月別=1 / 日別=2. Drives visibility of 施術報告書交付料（日別用）. */
  searchTermType?: 1 | 2;
}

/**
 * 印刷オプション panel for the 領収書・請求書 screen.
 * Mirrors A2 `rezepts/invoices.blade.php` line 578-754:
 *   - Left column: 帳票種類 (receipt/invoice/receipt_invoice) + 用紙サイズ (A4/A5/A6) + 10割 sub-checkboxes
 *   - Right column: 治療院/施術管理者/はり・きゅう師/受診者番号/日付/施術報告書/保険外明細 display options
 */
const RezeptInvoicePrintOptionsPanel: React.FC<RezeptInvoicePrintOptionsPanelProps> = ({
  control,
  watch,
  setValue,
  searchTermType,
}) => {
  const { serviceType } = useTheme();
  const isR2 = String(serviceType ?? '').toUpperCase() === SERVICE_TYPE_R2;
  const printType = watch('print_type');
  const option5   = watch('option_5');
  // 施術報告書交付料（日別用）only applies when printing per-day receipts. In 月別
  // mode the BE judges 報告料 from rezept.add_treatment_report / report dates in
  // the period — this checkbox would be misleading, so we hide it entirely.
  const isDaily = searchTermType === 2;

  // BD 4.20: when option_5 ticks ON, auto-focus + open the datepicker for option_date.
  const dateRef    = useRef<InputRef>(null);
  const prevOption5 = useRef(option5);
  useEffect(() => {
    if (!prevOption5.current && option5) {
      dateRef.current?.focus();
    }
    prevOption5.current = option5;
  }, [option5]);

  // A2 parity: print_type radio toggles which 10割 sub-checkbox is enabled, and
  // auto-adjusts option_9 / option_11 for A5 / A6.
  // BD 受診者番号: A4 = the user must tick it manually (not auto), A5/A6 = force-checked + disabled.
  useEffect(() => {
    if (printType === 1) {
      setValue('print_receipt_100_sub2', false);
      setValue('print_receipt_100_sub3', false);
      setValue('option_9', true);
      return;
    }
    if (printType === 2) {
      setValue('print_receipt_100_sub1', false);
      setValue('print_receipt_100_sub3', false);
      setValue('option_9', false);
      setValue('option_11', true);
      return;
    }
    if (printType === 3) {
      setValue('print_receipt_100_sub1', false);
      setValue('print_receipt_100_sub2', false);
      setValue('option_9', false);
      setValue('option_11', true);
    }
  }, [printType, setValue]);

  return (
    <CollapsibleCard title="印刷オプション" className="mt-4">
      <div className="grid grid-cols-5 gap-x-6">
        {/* Left column — 帳票種類 + 用紙サイズ (2/5) */}
        <div className="col-span-2 border border-blue-500 rounded p-3 space-y-2">
          <div className="font-semibold">帳票種類</div>
          <div className="grid grid-cols-3 gap-2">
            <Controller
              name="print_receipt"
              control={control}
              render={({ field }) => (
                <AppCheckbox checked={!!field.value} onChange={e => field.onChange(e.target.checked)}>
                  領収書
                </AppCheckbox>
              )}
            />
            <Controller
              name="print_invoice"
              control={control}
              render={({ field }) => (
                <AppCheckbox checked={!!field.value} onChange={e => field.onChange(e.target.checked)}>
                  請求書
                </AppCheckbox>
              )}
            />
            <Controller
              name="print_receipt_invoice"
              control={control}
              render={({ field }) => (
                <AppCheckbox checked={!!field.value} onChange={e => field.onChange(e.target.checked)}>
                  請求書兼領収書
                </AppCheckbox>
              )}
            />
          </div>

          <div className="font-semibold pt-2">用紙サイズ</div>
          <Controller
            name="print_type"
            control={control}
            render={({ field }) => (
              <AppRadioGroup
                value={field.value ?? undefined}
                onChange={e => field.onChange(Number(e.target.value))}
              >
                <div className="space-y-2">
                  <div className="flex items-center gap-3">
                    <AppRadio value={1}>A4</AppRadio>
                    <Controller
                      name="print_receipt_100_sub1"
                      control={control}
                      render={({ field: sub }) => (
                        <AppCheckbox
                          checked={!!sub.value}
                          disabled={printType !== 1}
                          onChange={e => sub.onChange(e.target.checked)}
                        >
                          10割
                        </AppCheckbox>
                      )}
                    />
                    <span className="text-xs font-semibold">※保険外明細はA4用紙で印刷してください</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <AppRadio value={2}>A5</AppRadio>
                    <Controller
                      name="print_receipt_100_sub2"
                      control={control}
                      render={({ field: sub }) => (
                        <AppCheckbox
                          checked={!!sub.value}
                          disabled={printType !== 2}
                          onChange={e => sub.onChange(e.target.checked)}
                        >
                          10割
                        </AppCheckbox>
                      )}
                    />
                    <span className="text-xs font-semibold">※A4用紙に2分割で印刷</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <AppRadio value={3}>A6</AppRadio>
                    <Controller
                      name="print_receipt_100_sub3"
                      control={control}
                      render={({ field: sub }) => (
                        <AppCheckbox
                          checked={!!sub.value}
                          disabled={printType !== 3}
                          onChange={e => sub.onChange(e.target.checked)}
                        >
                          10割
                        </AppCheckbox>
                      )}
                    />
                    <span className="text-xs font-semibold">※A4用紙に4分割で印刷</span>
                  </div>
                </div>
              </AppRadioGroup>
            )}
          />
        </div>

        {/* Right column — 表示オプション (3/5) */}
        <div className="col-span-3 space-y-2 p-3">
          <div className="grid grid-cols-2 gap-2">
            <Controller
              name="option_7"
              control={control}
              render={({ field }) => (
                <AppCheckbox checked={!!field.value} onChange={e => field.onChange(e.target.checked)}>
                  治療院基本情報の治療院名を表示
                </AppCheckbox>
              )}
            />
            <Controller
              name="option_8"
              control={control}
              render={({ field }) => (
                <AppCheckbox checked={!!field.value} onChange={e => field.onChange(e.target.checked)}>
                  治療院基本情報の住所と電話番号を表示
                </AppCheckbox>
              )}
            />
            <Controller
              name="option_1"
              control={control}
              render={({ field }) => (
                <AppCheckbox checked={!!field.value} onChange={e => field.onChange(e.target.checked)}>
                  施術管理者の名前を表示
                </AppCheckbox>
              )}
            />
            <Controller
              name="option_2"
              control={control}
              render={({ field }) => (
                <AppCheckbox checked={!!field.value} onChange={e => field.onChange(e.target.checked)}>
                  施術管理者の住所・電話番号を表示
                </AppCheckbox>
              )}
            />
            <Controller
              name="option_3"
              control={control}
              render={({ field }) => (
                <AppCheckbox checked={!!field.value} onChange={e => field.onChange(e.target.checked)}>
                  施術師の名前を表示
                </AppCheckbox>
              )}
            />
            <Controller
              name="option_4"
              control={control}
              render={({ field }) => (
                <AppCheckbox checked={!!field.value} onChange={e => field.onChange(e.target.checked)}>
                  はり・きゅう師拠点の住所・電話番号を表示
                </AppCheckbox>
              )}
            />
            <Controller
              name="option_11"
              control={control}
              render={({ field }) => (
                <AppCheckbox
                  checked={!!field.value}
                  disabled={printType === 2 || printType === 3}
                  onChange={e => field.onChange(e.target.checked)}
                >
                  受診者番号を表示
                </AppCheckbox>
              )}
            />
          </div>

          <div className="flex items-center gap-2 pt-1">
            <Controller
              name="option_5"
              control={control}
              render={({ field }) => (
                <AppCheckbox checked={!!field.value} onChange={e => field.onChange(e.target.checked)}>
                  日付入力
                </AppCheckbox>
              )}
            />
            <Controller
              name="option_date"
              control={control}
              render={({ field }) => (
                <RA2NDatePicker
                  ref={dateRef}
                  value={field.value || undefined}
                  onChange={raw => {
                    if (!raw) {
                      field.onChange('');
                      return;
                    }
                    const d = dayjs(parseDate(raw, DATEPICKER_FORMAT));
                    field.onChange(d.isValid() ? d.format('YYYY-MM-DD') : '');
                  }}
                  placeholder="元号00年00月00日"
                  startDate="1980-01-01"
                  endDate="2057-12-31"
                  className="w-[180px]"
                />
              )}
            />
          </div>

          {(isDaily || !isR2) && (
            <div className="flex items-center gap-4 pt-1">
              {isDaily && (
                <Controller
                  name="option_6"
                  control={control}
                  render={({ field }) => (
                    <AppCheckbox checked={!!field.value} onChange={e => field.onChange(e.target.checked)}>
                      施術報告書交付料（日別用）
                    </AppCheckbox>
                  )}
                />
              )}
              {!isR2 && (
                <Controller
                  name="option_9"
                  control={control}
                  render={({ field }) => (
                    <AppCheckbox
                      checked={!!field.value}
                      disabled={printType === 2 || printType === 3}
                      onChange={e => field.onChange(e.target.checked)}
                    >
                      保険外明細
                    </AppCheckbox>
                  )}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </CollapsibleCard>
  );
};

export default RezeptInvoicePrintOptionsPanel;
